/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useState } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import {
  SegmentCreatorData,
  useSegmentCreatorContext,
} from '@insights_module/context/SegmentCreatorContext';
import {
  AQLQueryCondition,
  AQLQueryType,
  AQLQueryValue,
  AQLRangeCondition,
} from '@insights_module/adc_ql/types';
import { useFilterTypeOptions } from '@insights_module/hooks/useFilterTypeOptions';
import { DatePicker } from '@components/DatePicker';

const getRangeCondition = (value: AQLQueryValue): string | null => {
  if (typeof value !== 'object' || !value) return null;

  const keys = Object.keys(value as object);
  if (keys.length === 1 && keys[0] === 'gte') return 'on_or_after';
  if (keys.length === 1 && keys[0] === 'lte') return 'on_or_before';
  return 'range';
};

const getExactDateValue = (value: AQLQueryValue): Date | null => {
  const condition = typeof value === 'string' ? 'eq' : getRangeCondition(value);
  if (condition === 'eq' && typeof value === 'string') {
    return new Date(value);
  }
  if (
    condition === 'on_or_after' &&
    typeof value === 'object' &&
    (value as AQLRangeCondition<string>).gte
  ) {
    return new Date((value as AQLRangeCondition<string>).gte as string);
  }
  if (
    condition === 'on_or_before' &&
    typeof value === 'object' &&
    (value as AQLRangeCondition<string>).lte
  ) {
    return new Date((value as AQLRangeCondition<string>).lte as string);
  }
  return null;
};

const getRangeValue = (value: AQLQueryValue): [Date | null, Date | null] => {
  if (typeof value === 'string') {
    return [null, null];
  }

  const condition = getRangeCondition(value);
  if (
    condition === 'range' &&
    typeof value === 'object' &&
    (value as AQLRangeCondition<string>).gte &&
    (value as AQLRangeCondition<string>).lte
  ) {
    return [
      new Date((value as AQLRangeCondition<string>).gte as string),
      new Date((value as AQLRangeCondition<string>).lte as string),
    ];
  }
  return [null, null];
};

export const SegmentCreatorPublishedDateItem = ({
  segmentData,
}: {
  segmentData: SegmentCreatorData;
}) => {
  const { value, condition, type } = segmentData;
  const { t } = useTranslation();
  const { pushSegmentData, isPresentational } = useSegmentCreatorContext();
  const { dropdownOptions } = useFilterTypeOptions();

  const conditionOptions: IDropdownOption[] = [
    {
      value: 'on_or_after',
      label: <Option>{t('insights.query_builder.conditions.on_or_after')}</Option>,
    },
    {
      value: 'on_or_before',
      label: <Option>{t('insights.query_builder.conditions.on_or_before')}</Option>,
    },
    { value: 'range', label: <Option>{t('insights.query_builder.conditions.range')}</Option> },
    { value: 'eq', label: <Option>{t('insights.query_builder.conditions.exact')}</Option> },
  ];

  const [selectedType, setSelectedType] = useState<IDropdownOption>(
    dropdownOptions.find((item) => item.value === segmentData.type) || dropdownOptions[0]
  );

  const [selectedCondition, setSelectedCondition] = useState<IDropdownOption>(
    conditionOptions.find((item) => {
      if (condition === ('eq' as unknown as AQLQueryCondition) && item.value === 'eq') return true;
      if (
        condition === ('range' as unknown as AQLQueryCondition) &&
        item.value === getRangeCondition(value as AQLQueryValue)
      )
        return true;
    }) || conditionOptions[2]
  );

  const [exactValue, setExactValue] = useState<Date | null>(
    getExactDateValue(value as AQLQueryValue)
  );

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
    getRangeValue(value as AQLQueryValue)
  );

  useEffect(() => {
    if (selectedType && selectedType.value !== type) {
      const type = selectedType.value as string;
      pushSegmentData({
        ...segmentData,
        type: type as AQLQueryType,
        category: null,
        value: null,
        condition: null,
        negation: false,
      });
    }
  }, [selectedType]);

  const resetValues = () => {
    setExactValue(null);
    setDateRange([null, null]);
  };

  useEffect(() => {
    if (
      (selectedCondition && selectedCondition.value !== 'range' && exactValue) ||
      (selectedCondition && selectedCondition.value === 'range' && dateRange[0] && dateRange[1])
    ) {
      const condition = selectedCondition.value === 'eq' ? 'eq' : 'range';
      let value;
      if (selectedCondition.value === 'range' && dateRange[0] && dateRange[1]) {
        value = {
          gte: dateRange[0].toISOString(),
          lte: dateRange[1].toISOString(),
        };
      }
      if (selectedCondition.value === 'eq' && exactValue) {
        value = exactValue.toISOString();
      }
      if (selectedCondition.value === 'on_or_after' && exactValue) {
        value = {
          gte: exactValue.toISOString(),
        };
      }
      if (selectedCondition.value === 'on_or_before' && exactValue) {
        value = {
          lte: exactValue.toISOString(),
        };
      }
      const segment = {
        ...segmentData,
        condition: condition as unknown as Exclude<AQLQueryCondition, 'eq' | 'range'>,
        value: value,
      } as SegmentCreatorData;
      pushSegmentData(segment);
    }
  }, [selectedCondition, dateRange, exactValue]);

  return (
    <div tw='flex gap-1 items-center'>
      <Dropdown
        options={dropdownOptions}
        value={selectedType}
        onChange={(val) => setSelectedType(val as IDropdownOption)}
        labelStyle={tw`bg-spec-publish-date-filter-bg border border-spec-publish-date-filter-stroke text-spec-publish-date-filter-text font-semibold`}
        caretStyle={tw`fill-spec-publish-date-filter-text`}
        dropdownMenuStyle={tw`w-[178px]`}
        isPresentational={isPresentational}
      />

      <Dropdown
        options={conditionOptions}
        value={selectedCondition}
        onChange={(val) => {
          setSelectedCondition(val as IDropdownOption);
          if ((val as IDropdownOption).value !== selectedCondition.value) {
            resetValues();
          }
        }}
        isSearchable={false}
        isPresentational={isPresentational}
      />

      <div css={[selectedCondition.value === 'range' ? tw`w-[180px]` : tw`w-[105px]`]}>
        <DatePicker
          tw='h-[28px] w-full border border-gray-1 focus:border-accent-2 shadow-dropdown text-xs font-medium bg-neutral-background'
          // @ts-ignore
          placeholderText={
            selectedCondition.value === 'range'
              ? t('insights.query_builder.published_date.range_placeholder')
              : t('insights.query_builder.published_date.exact_placeholder')
          }
          selectsRange={selectedCondition.value === 'range'}
          selected={exactValue}
          startDate={dateRange[0]}
          endDate={dateRange[1]}
          onChange={(update: [Date | null, Date | null] | Date | null): void => {
            if (selectedCondition.value === 'range') {
              setDateRange(update as unknown as [Date | null, Date | null]);
            } else {
              setExactValue(update as unknown as Date | null);
            }
          }}
          disabled={isPresentational}
        />
      </div>
    </div>
  );
};
